import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import type { components } from '@/lib/api-types';

/**
 * The fortnight calendar — the one query the application chrome owns.
 *
 * Every page endpoint is fortnight-scoped, and `isCurrent` is computed
 * server-side ("the latest fortnight whose meeting date is not in the
 * future", decision D2) precisely so no browser has to reimplement the
 * calendar. This hook is therefore the single place the SPA learns which
 * fortnight it is looking at; `Topbar` renders it, pages key their queries on
 * it.
 *
 * `limit: 200` is the documented maximum and not an optimisation: the
 * calendar runs to FN-78 and the server pages newest-first, so the default 50
 * stops at FN-29 and the *current* fortnight is simply not in the response.
 * A selector that cannot see today is worse than no selector.
 */

export type FortnightRead = components['schemas']['FortnightRead'];

/** Server maximum. The whole 2026–2028 calendar fits inside it. */
const CALENDAR_LIMIT = 200;

export const fortnightKeys = {
  all: ['fortnights'] as const,
  list: (limit: number) => ['fortnights', 'list', limit] as const,
  pulse: (fnId: string) => ['fortnights', fnId, 'pulse'] as const,
};

export function useFortnights(): UseQueryResult<components['schemas']['FortnightPage']> {
  return useQuery({
    queryKey: fortnightKeys.list(CALENDAR_LIMIT),
    queryFn: ({ signal }) => api.fortnights.list({ limit: CALENDAR_LIMIT }, { signal }),
  });
}

export interface CurrentFortnight {
  /** The fortnight the meeting is about, or `null` until it resolves. */
  readonly fortnight: FortnightRead | null;
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * The current fortnight, or `null`.
 *
 * `null` is returned for "still loading", "the request failed" and "the
 * calendar is empty" alike, because every caller does the same thing with all
 * three: render its own skeleton or empty state rather than guess a fortnight
 * id. Callers that need to tell them apart read `isPending` / `isError`.
 */
export function useCurrentFortnight(): CurrentFortnight {
  const query = useFortnights();
  const rows = query.data?.rows ?? [];

  return {
    fortnight: rows.find((row) => row.isCurrent) ?? null,
    isPending: query.isPending,
    isError: query.isError,
  };
}
